import { Link, Navigate, Outlet } from 'react-router';
import { Button, NotFound, Wordmark } from '../../ui';
import { useSession } from '../session/SessionProvider';
import { AdminNav } from './AdminNav';

/**
 * The coach's surface, and its guard.
 *
 * `/admin/*` renders **not found** — not a redirect — for a signed-in person
 * who is not a coach. A redirect to their own surface would confirm that the
 * admin area exists and that they are on the wrong side of it;
 * `access-and-consent` requires no hint that content exists (design.md F5).
 *
 * A visitor with no session at all is a different case: nothing has been
 * revealed to them, and sending them to the sign-in form is what they want.
 */
export function AdminLayout() {
  const { status, profile, profileLoaded, signOut } = useSession();

  if (status === 'loading' || (status === 'authenticated' && !profileLoaded)) {
    return <Loading />;
  }

  if (status === 'anonymous') return <Navigate to="/admin/sign-in" replace />;

  if (profile?.role !== 'coach') return <NotFound />;

  return (
    <div className="bg-surface min-h-dvh">
      <header className="border-border bg-surface-raised border-b">
        {/*
          One row on a laptop, two on a phone: the nav takes `order-last w-full`
          below `sm`, so the wordmark and Sign out keep the top line at 360px and
          the links sit under them rather than squeezing the coach's name. The
          wordmark stays a link to the roster, as it was when it was the only
          way back.
        */}
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
          <Link to="/admin" className="flex items-center gap-2">
            <Wordmark name={profile.display_name} size="md" />
          </Link>

          <div className="order-last w-full sm:order-none sm:w-auto">
            <AdminNav />
          </div>

          <Button
            variant="ghost"
            className="ml-auto"
            onClick={() => {
              void signOut();
            }}
          >
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-4xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

export function Loading() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-4">
      <p className="text-ink-muted text-sm">Loading…</p>
    </main>
  );
}
